import { useUserScores } from '@/hooks/useUserScores';
import { useWeeklyActivity } from '@/hooks/useWeeklyActivity';
import { computeMedalTier } from '@/utils/scoring';
import { Ionicons } from '@expo/vector-icons';
import * as Haptics from 'expo-haptics';
import { LinearGradient } from 'expo-linear-gradient';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    ScrollView,
    StatusBar,
    StyleSheet,
    Text,
    TouchableOpacity,
    View
} from 'react-native';
import ConfettiBurst from '@/components/ConfettiBurst';
import { SafeAreaView } from 'react-native-safe-area-context';
import BronzeMedal from '../assets/images/icons/bronze.svg';
import GoldMedal from '../assets/images/icons/gold.svg';
import LocalProductsIcon from '../assets/images/icons/local_products.svg';
import RecyclableIcon from '../assets/images/icons/recyclable.svg';
import SoybeanIcon from '../assets/images/icons/soybean.svg';
import SilverMedal from '../assets/images/icons/silver.svg';

export default function CelebrationScreen() {
  const { t } = useTranslation();
  const router = useRouter();
  const params = useLocalSearchParams();

  // Get points from route params or use defaults
  const totalPoints = Number(params.totalPoints) || 0;
  const actionPoints = Number(params.actionPoints) || totalPoints;
  const bonusPoints = Number(params.bonusPoints) || 0;
  const zeroWastePoints = Number(params.zeroWastePoints) || 0;
  const balancedFoodPoints = Number(params.balancedFoodPoints) || 0;
  const localFoodPoints = Number(params.localFoodPoints) || 0;
  const selectedActionsCount = Number(params.selectedActionsCount) || 0;
  const actionsToday = Number(params.actionsToday) || 0;
  const allActionsDone = params.allActionsDone === '1';

  // Medal tier — same source of truth as the profil tab so they stay in
  // sync. Based on the number of actions completed today (out of 9).
  const { scores, refetch: refetchScores } = useUserScores();
  const earnedMedal = computeMedalTier(actionsToday);

  // Get real weekly activity data from database
  const { weeklyActivity, loading: activityLoading, refetch: refetchActivity } = useWeeklyActivity();

  // Refetch on mount to get fresh data after saving actions.
  useEffect(() => {
    refetchActivity();
    refetchScores();
  }, []);

  useEffect(() => {
    // Trigger MAXIMUM haptic feedback - full dopamine mode! 🎉
    const triggerHaptics = async () => {
      try {
        // Triple heavy impact for maximum effect
        await Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Heavy);
        setTimeout(() => Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Heavy), 50);
        setTimeout(() => Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Heavy), 100);
        setTimeout(() => Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Heavy), 150);
        setTimeout(() => Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium), 250);
        setTimeout(() => Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium), 350);
        setTimeout(() => Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success), 500);
      } catch (error) {
        // Haptics not available on this device
      }
    };

    triggerHaptics();

  }, []);

  const maxPoints = Math.max(...weeklyActivity.map(d => d.points));

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: '#FBEFE3' }} edges={['top']}>
      <Stack.Screen options={{ headerShown: false }} />
      <StatusBar barStyle="dark-content" backgroundColor="#FBEFE3" />

      <LinearGradient
        colors={['#FBEFE3', '#FFFFFF']}
        locations={[0, 1]}
        style={styles.container}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          {/* Header - Title on left, Accueil on right */}
          <View style={styles.header}>
            <Text style={styles.title}>{t('celebration.title')}</Text>
            <TouchableOpacity
              style={styles.homeLink}
              onPress={() => router.push('/(tabs)')}
            >
              <Text style={styles.homeLinkText}>{t('celebration.homeLink')}</Text>
              <Ionicons name="chevron-forward" size={16} color="#8B4513" />
            </TouchableOpacity>
          </View>

          {allActionsDone && (
            <View style={styles.allDoneCard}>
              <Text style={styles.allDoneEmoji}>🎉</Text>
              <Text style={styles.allDoneTitle}>{t('celebration.allDoneTitle')}</Text>
              <Text style={styles.allDoneText}>{t('celebration.allDoneText')}</Text>
            </View>
          )}

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>{t('celebration.resultsTitle')}</Text>
            <View style={styles.resultsContainer}>
              <View style={styles.pointRow}>
                <View style={styles.pointHeader}>
                  <View style={styles.pointIconContainer}>
                    {React.createElement(GoldMedal, { width: 24, height: 24 })}
                  </View>
                  <Text style={styles.pointLabel}>{t('celebration.totalPointsLabel')}</Text>
                  <Text style={styles.pointValue}>{t('common.pointsFull', { points: totalPoints })}</Text>
                </View>
                <View style={styles.pointBarContainer}>
                  <LinearGradient
                    colors={['#A8D475', '#83BF4F']}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 0 }}
                    style={[
                      styles.pointBar,
                      { width: `${Math.min((totalPoints / 270) * 100, 100)}%` },
                    ]}
                  />
                </View>
              </View>

              {bonusPoints > 0 && (
                <View style={styles.pointRow}>
                  <View style={styles.pointHeader}>
                    <View style={styles.pointIconContainer}>
                      <Ionicons name="trophy" size={22} color="#CD7728" />
                    </View>
                    <Text style={styles.pointLabel}>{t('celebration.bonusLabel')}</Text>
                    <Text style={[styles.pointValue, { color: '#CD7728' }]}>
                      {t('celebration.bonusValue', { points: bonusPoints })}
                    </Text>
                  </View>
                  <View style={styles.pointBarContainer}>
                    <LinearGradient
                      colors={['#FCC189', '#CD7728']}
                      start={{ x: 0, y: 0 }}
                      end={{ x: 1, y: 0 }}
                      style={[
                        styles.pointBar,
                        { width: `${Math.min((bonusPoints / 150) * 100, 100)}%` },
                      ]}
                    />
                  </View>
                </View>
              )}

              {/* Zero Waste Points */}
              <View style={styles.pointRow}>
                <View style={styles.pointHeader}>
                  <View style={styles.pointIconContainer}>
                    {React.createElement(RecyclableIcon, { width: 24, height: 24 })}
                  </View>
                  <Text style={styles.pointLabel}>{t('celebration.zeroWasteLabel')}</Text>
                  <Text style={styles.pointValue}>{t('common.pointsFull', { points: zeroWastePoints })}</Text>
                </View>
                <View style={styles.pointBarContainer}>
                  <LinearGradient
                    colors={['#A8D475', '#83BF4F']}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 0 }}
                    style={[
                      styles.pointBar,
                      { width: `${(zeroWastePoints / 15) * 100}%` },
                    ]}
                  />
                </View>
              </View>

              {/* Balanced Food Points */}
              <View style={styles.pointRow}>
                <View style={styles.pointHeader}>
                  <View style={styles.pointIconContainer}>
                    {React.createElement(SoybeanIcon, { width: 24, height: 24 })}
                  </View>
                  <Text style={styles.pointLabel}>{t('celebration.balancedLabel')}</Text>
                  <Text style={styles.pointValue}>{t('common.pointsFull', { points: balancedFoodPoints })}</Text>
                </View>
                <View style={styles.pointBarContainer}>
                  <LinearGradient
                    colors={['#A8D475', '#83BF4F']}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 0 }}
                    style={[
                      styles.pointBar,
                      { width: `${(balancedFoodPoints / 15) * 100}%` },
                    ]}
                  />
                </View>
              </View>

              {/* Local Food Points */}
              <View style={styles.pointRow}>
                <View style={styles.pointHeader}>
                  <View style={styles.pointIconContainer}>
                    {React.createElement(LocalProductsIcon, { width: 24, height: 24 })}
                  </View>
                  <Text style={styles.pointLabel}>{t('celebration.localLabel')}</Text>
                  <Text style={styles.pointValue}>{t('common.pointsFull', { points: localFoodPoints })}</Text>
                </View>
                <View style={styles.pointBarContainer}>
                  <LinearGradient
                    colors={['#A8D475', '#83BF4F']}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 0 }}
                    style={[
                      styles.pointBar,
                      { width: `${(localFoodPoints / 15) * 100}%` },
                    ]}
                  />
                </View>
              </View>
            </View>
          </View>

          {/* Weekly Activity */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>{t('celebration.weeklyActivityTitle')}</Text>
            <View style={styles.activityContainer}>
              {weeklyActivity.map((item, index) => (
                <View key={index} style={styles.activityDay}>
                  <View style={styles.barContainer}>
                    {item.points > 0 ? (
                      <LinearGradient
                        colors={['#A8D475', '#83BF4F']}
                        start={{ x: 0, y: 0 }}
                        end={{ x: 0, y: 1 }}
                        style={[
                          styles.activityBar,
                          { height: maxPoints > 0 ? `${(item.points / maxPoints) * 100}%` : '0%' },
                        ]}
                      >
                        <Text style={styles.barLabel}>{item.points}</Text>
                      </LinearGradient>
                    ) : (
                      <View 
                        style={[
                          styles.activityBar,
                          { height: '0%' },
                        ]}
                      />
                    )}
                  </View>
                  {/* item.day (hook) est un libellé FR figé → on re-dérive le
                      libellé localisé depuis item.dayOfWeek via dates.*. */}
                  <Text style={styles.dayLabel}>
                    {t(`dates.weekdayShort${item.dayOfWeek % 7}`)}.
                  </Text>
                </View>
              ))}
            </View>
          </View>

          {/* Medals Section */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>{t('celebration.medalsTitle')}</Text>
            <View style={styles.medalsContainer}>
              <View style={[styles.medalItem, earnedMedal !== 'bronze' && styles.medalItemInactive]}>
                <BronzeMedal width={60} height={60} opacity={earnedMedal !== 'bronze' ? 0.3 : 1} />
                <Text style={[styles.medalLabel, earnedMedal !== 'bronze' && styles.medalLabelInactive]}>
                  {t('celebration.medalBronze')}
                </Text>
                <Text style={styles.medalRange}>{t('celebration.medalRangeBronze')}</Text>
              </View>
              <View style={[styles.medalItem, earnedMedal !== 'silver' && styles.medalItemInactive]}>
                <SilverMedal width={60} height={60} opacity={earnedMedal !== 'silver' ? 0.3 : 1} />
                <Text style={[styles.medalLabel, earnedMedal !== 'silver' && styles.medalLabelInactive]}>
                  {t('celebration.medalSilver')}
                </Text>
                <Text style={styles.medalRange}>{t('celebration.medalRangeSilver')}</Text>
              </View>
              <View style={[styles.medalItem, earnedMedal !== 'gold' && styles.medalItemInactive]}>
                <GoldMedal width={60} height={60} opacity={earnedMedal !== 'gold' ? 0.3 : 1} />
                <Text style={[styles.medalLabel, earnedMedal !== 'gold' && styles.medalLabelInactive]}>
                  {t('celebration.medalGold')}
                </Text>
                <Text style={styles.medalRange}>{t('celebration.medalRangeGold')}</Text>
              </View>
            </View>
          </View>
        </ScrollView>
      </LinearGradient>

      <ConfettiBurst count={150} />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollContent: {
    padding: 24,
    paddingTop: 0,
    paddingBottom: 24,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 24,
    paddingTop:24
  },
  title: {
    fontFamily: 'Inter_700Bold',
    fontSize: 28,
    color: '#000',
  },
  homeLink: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  homeLinkText: {
    fontFamily: 'Inter_300Light',
    fontSize: 14,
    color: '#8B4513',
  },
  allDoneCard: {
    backgroundColor: '#1C453F',
    borderRadius: 16,
    padding: 20,
    marginBottom: 24,
    alignItems: 'center',
  },
  allDoneEmoji: {
    fontSize: 36,
    marginBottom: 8,
  },
  allDoneTitle: {
    fontFamily: 'Inter_700Bold',
    fontSize: 18,
    color: '#FFF',
    marginBottom: 8,
    textAlign: 'center',
  },
  allDoneText: {
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    color: '#FBEFE3',
    textAlign: 'center',
    lineHeight: 20,
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontFamily: 'Inter_700Bold',
    fontSize: 16,
    color: '#000',
    marginBottom: 12,
  },
  resultsContainer: {
    backgroundColor: '#FFF',
    borderRadius: 12,
    paddingLeft: 16,
    paddingRight: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
  },
  pointRow: {
    paddingTop: 12,
    paddingBottom: 12,
    borderBottomColor: '#F5F5F5',
  },
  pointHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  pointIconContainer: {
    width: 24,
    height: 24,
    marginRight: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  pointLabel: {
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    color: '#000',
    flex: 1,
  },
  pointValue: {
    fontFamily: 'Inter_700Bold',
    fontSize: 12,
    color: '#000',
    minWidth: 60,
    textAlign: 'right',
  },
  pointBarContainer: {
    flex: 1,
    height: 8,
    backgroundColor: '#F0F0F0',
    borderRadius: 4,
    overflow: 'hidden',
  },
  pointBar: {
    height: '100%',
    borderRadius: 4,
  },
  activityContainer: {
    backgroundColor: '#FFF',
    borderRadius: 12,
    padding: 24,
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'flex-end',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
    minHeight: 180,
  },
  activityDay: {
    alignItems: 'center',
    flex: 1,
  },
  barContainer: {
    height: 120,
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginBottom: 8,
  },
  activityBar: {
    width: 40,
    backgroundColor: '#E5E5E5',
    borderRadius: 8,
    justifyContent: 'flex-start',
    alignItems: 'center',
    paddingTop: 8,
    minHeight: 40,
  },
  barLabel: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: '#FFF',
  },
  dayLabel: {
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    color: '#666',
  },
  medalsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    backgroundColor: '#FFF',
    borderRadius: 12,
    padding: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
  },
  medalItem: {
    alignItems: 'center',
  },
  medalItemInactive: {
    opacity: 0.5,
  },
  medalLabel: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: '#000',
    marginTop: 8,
  },
  medalLabelInactive: {
    color: '#999',
  },
  medalRange: {
    fontFamily: 'Inter_400Regular',
    fontSize: 11,
    color: '#666',
    marginTop: 2,
  },
});
